import { Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, TextField, Theme, Tooltip, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ReactNode, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import E from '../../../felles/Enums';
import U from '../../../felles/Utils';
import FirmNavnId from '../../../models/FirmNavnId';
import * as actions from '../../../store/actions/index';
import { AppState } from '../../../store/rootReducer';


import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import "dayjs/locale/nb";
import { TaleFavorittListe, TaleSearchParams } from '../../../models/TalenesTale';
import StorePicker, { storeIds, storeNames } from './StorePicker';
import { shownColumns } from './SalgsstatistikkTable';

// Compact sizing taken from NO04Search (34px fields, .85rem text).
const useStyles = makeStyles()((theme: Theme) => ({
    eyebrow: { fontSize: '0.72rem', fontWeight: 600, letterSpacing: '.08em', color: theme.palette.text.secondary, textTransform: 'uppercase', },
    row: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: theme.spacing(1.5), },
    fieldLabel: { display: 'block', fontSize: '0.78rem', color: theme.palette.text.secondary, marginBottom: 2, },
    field: {
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root, & .MuiPickersInputBase-root': { height: '34px', fontSize: '0.85rem', paddingTop: 0, paddingBottom: 0, },
        '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '0.85rem', },
        '& .MuiPickersSectionList-root': { padding: '6px 0', fontSize: '0.85rem', },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': { borderColor: '#000', },
    },
    checkbox: { marginLeft: 0, marginRight: theme.spacing(2), '& .MuiFormControlLabel-label': { fontSize: '0.85rem', }, '& .MuiCheckbox-root': { padding: 4, marginRight: 2, }, },
    button: { height: '32px', textTransform: 'none', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap', paddingLeft: theme.spacing(1.5), paddingRight: theme.spacing(1.5), },
    outlined: { borderColor: '#c4c4c4', color: '#2E2D2C', '&:hover': { borderColor: '#2E2D2C', backgroundColor: '#f5f5f5', }, },
}));

// Excel / PDF buttons as in SS12.
const EXPORT_BUTTON_SX = { height: 30, fontSize: '0.8rem', textTransform: 'none' } as const;

/** Small label above a field, as in the design (instead of MUI's floating label). */
const Field = ({ label, width, children }: { label: string; width: number; children: ReactNode }) => {
    const { classes } = useStyles();
    return (
        <Box sx={{ width, flexShrink: 0 }}>
            <span className={classes.fieldLabel}>{label}</span>
            {children}
        </Box>
    );
};

//
const TallenesTaleTableToolbar = (/*props*/) => {
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const firmListFromAdmin: FirmNavnId[] = useSelector((state) => (state as AppState).userAdmin.firmListFromAdmin);
    const isFetchingAdminFirmData: boolean = useSelector((state) => (state as AppState).userAdmin.isFetchingAdminFirmData);
    const taleFavorittListeList: TaleFavorittListe[] = useSelector((state) => (state as AppState).tallenesTale.taleFavorittListe);
    const isFetchingFavorittListe: boolean = useSelector((state) => (state as AppState).tallenesTale.isFetchingFavorittListe);
    const selectedSubKunde: FirmNavnId = useSelector((state) => (state as AppState).tallenesTale.selectedSubKunde);
    const isFetchingSalesReport: boolean = useSelector((state) => (state as AppState).tallenesTale.isFetchingSalesReport);
    // The file being made ("Lager…" on its button), until the request is done.
    const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
    useEffect(() => {
        if (!isFetchingSalesReport) setExporting(null);
    }, [isFetchingSalesReport]);
    const [buttonSaveDisabled, setButtonSaveDisabled] = useState(false);
    const [buttonDeleteDisabled, setButtonDeleteDisabled] = useState(false);
    const [buttonReportDisabled, setButtonReportDisabled] = useState(false);
    const date = useSelector(state => (state as AppState).accounting.date);
    const userJWT = useSelector((state) => (state as AppState).auth.userJWT);

    const [buttonDataDisabled, setButtonDataDisabled] = useState(false);
    const [isBruttoFortjeneste, setIsBruttoFortjeneste] = useState(true);

    //
    const methods = useForm({
        defaultValues: {
            fList: null,
            bList: [] as FirmNavnId[]/*, maned: null*/,
            startDate: dayjs(date).set('month', 0).startOf('month'),
            endDate: dayjs(date).endOf('month'),
            visning: null,
            brutto: false,
            bruttofortjeneste: false,
            listId: 0,
            betegnelse: '',
            merknader: ''
        }
    });

    const favListObj: any = methods.watch("fList");
    const betegnelseObj: any = methods.watch("betegnelse");
    const startDateObj: any = methods.watch("startDate");
    const endDateObj: any = methods.watch("endDate");
    const visningObj: any = methods.watch("visning");
    const bListObj: any = methods.watch("bList");
    const hasButikk = bListObj?.length > 0;
    // The logged-in company - "Hovedfirma" in Butikk liste, and the default selection.
    const hovedfirma = new FirmNavnId(Number(U.T_companyNr), userJWT.CompanyName, 0);

    // The favorite lists and the store list, once.
    useEffect(() => {
        if (!isFetchingFavorittListe && (taleFavorittListeList ?? []).length === 0)
            dispatch(actions.favorittListeGet());
        if (!isFetchingAdminFirmData && (firmListFromAdmin ?? []).length === 0)
            dispatch(actions.firmListFromDBGet());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The favorite's store, by id - also when the store list arrives after the favorite was picked.
    useEffect(() => {
        if (!favListObj) return;
        const store = [hovedfirma, ...(firmListFromAdmin ?? [])].find((f) => Number(f.firmID) === Number(favListObj.butikkId));
        methods.setValue('bList', store ? [store] : [], { shouldDirty: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favListObj, firmListFromAdmin]);

    useEffect(() => {
        if (favListObj) {
            dispatch(actions.setFullResetSalesReported());
            dispatch(actions.setSelectedKunde(new FirmNavnId(favListObj?.butikkId, favListObj?.butikk)));
            dispatch(actions.setSelectedSubKunde(new FirmNavnId(favListObj?.subButikkId, favListObj?.subButikk)));
            methods.setValue('startDate', favListObj.startDate, { shouldDirty: true });
            methods.setValue('endDate', favListObj.endDate, { shouldDirty: true });
            methods.setValue('visning', favListObj.visning, { shouldDirty: true });
            methods.setValue('brutto', favListObj.brutto, { shouldDirty: true });
            methods.setValue('bruttofortjeneste', favListObj.bruttofortjeneste, { shouldDirty: true });
            methods.setValue('listId', favListObj.id, { shouldDirty: true });
            methods.setValue('betegnelse', favListObj.betegnelse, { shouldDirty: true });
            methods.setValue('merknader', favListObj.merknader, { shouldDirty: true });
            dispatch(actions.setfavorittListeVisningType(favListObj.visning))
        } else {
            methods.setValue('bList', [], { shouldDirty: true });
            methods.setValue('listId', 0, { shouldDirty: true });
            methods.setValue('startDate', dayjs(date).set('month', 0).startOf('month'), { shouldDirty: true });
            methods.setValue('endDate', dayjs(date).startOf('month'), { shouldDirty: true });
            methods.setValue('visning', null, { shouldDirty: true });
            methods.setValue('brutto', false, { shouldDirty: true });
            methods.setValue('bruttofortjeneste', false, { shouldDirty: true });
            methods.setValue('betegnelse', '', { shouldDirty: true });
            methods.setValue('merknader', '', { shouldDirty: true });
            dispatch(actions.setSelectedKunde(new FirmNavnId(0, '')));
            dispatch(actions.setSelectedSubKunde(new FirmNavnId(0, '')));
            dispatch(actions.setfavorittListeVisningType(undefined));
        }
    }, [favListObj]);
    //
    useEffect(() => {
        //
        if (favListObj === null) {
            setButtonSaveDisabled(true);
            setButtonDeleteDisabled(true);
        } else {
            setButtonSaveDisabled(false);
            setButtonDeleteDisabled(false);
        }
        //
        if (favListObj?.visning !== betegnelseObj) {
            setButtonDeleteDisabled(true);
        }
        //
        if (!hasButikk || startDateObj === null || endDateObj === null || visningObj === null || visningObj === '') {
            setButtonReportDisabled(true);
            setButtonDataDisabled(true);
        } else {
            setButtonReportDisabled(false);
            setButtonDataDisabled(true);
        }
        //
        if (favListObj === null && betegnelseObj !== '' && hasButikk && startDateObj !== null && endDateObj !== null && visningObj !== null) {
            setButtonSaveDisabled(false);
        } else if (favListObj !== null) {
            setButtonDeleteDisabled(false);
        }
        // A favorittliste holds one store (TaleFavorittListe.ButikkId is a single id).
        if (bListObj?.length > 1) {
            setButtonSaveDisabled(true);
        }

        if (hasButikk && startDateObj !== null && endDateObj !== null && (visningObj === '' || visningObj === null)) {

            setButtonDataDisabled(false);

        } else if (hasButikk && startDateObj !== null && endDateObj !== null && visningObj !== '') {
            setButtonDataDisabled(true);
        }


        // Enabled Rådata button to enabled rådata report on datter companies
        if (visningObj === 'Kunde' && selectedSubKunde.firmID !== 0) {
            setButtonDataDisabled(false);
        }

    }, [bListObj, startDateObj, visningObj, favListObj, betegnelseObj, firmListFromAdmin, selectedSubKunde]);

    useEffect(() => {
        methods.setValue(`bList`, [hovedfirma], { shouldDirty: true });
    }, []);

    //
    const onSubmitSaveFavorite = (o: any) => {
        dispatch(
            actions.favorittListeUpdated(
                new TaleFavorittListe(
                    o.betegnelse !== o.fList?.betegnelse ? 0 : o.fList.id, '', o.bList[0]?.firmID || '0',
                    o.bList[0]?.firmName, o.startDate, o.endDate, o.visning, o.brutto, o.bruttofortjeneste, o.betegnelse, o.merknader, selectedSubKunde?.firmID, selectedSubKunde?.firmName)
            )
        );
    };

    //
    const onSubmitDeleteFavorite = (o: any = undefined) => {
        dispatch(actions.favorittListeListSelectedDeletion(o.listId));
    };

    //
    const checkIfItAlreadyExist = (val: string) => {
        if (taleFavorittListeList?.length > 0) {
            const favListObjExists: any = taleFavorittListeList?.filter((item) => item.betegnelse.trim() === val);
            if (favListObjExists.length > 0) {
                methods.setValue('fList', favListObjExists[0], { shouldDirty: true });
            } else {
                methods.setValue('bList', [], { shouldDirty: true });
                methods.setValue('listId', 0, { shouldDirty: true });
                methods.setValue('visning', null, { shouldDirty: true });
                methods.setValue('brutto', false, { shouldDirty: true });
                methods.setValue('bruttofortjeneste', false, { shouldDirty: true });
            }
        }
    };

    const onSubmitGetSalesReport = (o: any = undefined, mediaType = 'web') => {
        //
        const displayDates =
            dayjs(o.startDate).format('YYYY-MM-DD').substring(0, 7) + ' - ' + dayjs(o.endDate).endOf('month').format('YYYY-MM-DD').substring(0, 7)
            + ' ' +
            dayjs(o.startDate).subtract(1, 'year').format('YYYY-MM-DD').substring(0, 7) + ' - ' + dayjs(o.endDate).subtract(1, 'year').endOf('month').format('YYYY-MM-DD').substring(0, 7);
        //
        const currentDate = dayjs(date).format('YYYYMM');
        const startDate = dayjs(o.startDate).format('YYYYMM');
        const endDate = dayjs(o.endDate).format('YYYYMM');
        // Default
        // Several ticked stores go as "963,964,965" and come back as one report summed together.
        const taleSearchParams = new TaleSearchParams(U.T_parentId, storeIds(o.bList) || '0'
            , storeNames(o.bList)
            , '', '', '', ''
            , mediaType === 'data' ? 'data' : o.visning, o.brutto, o.bruttofortjeneste, mediaType, displayDates, selectedSubKunde?.firmID, selectedSubKunde?.firmName
            , '', '', '', '',)
        // Only get data with faks__fakt_omg - range is tomongh previous the current date.
        /**/
        if (currentDate === startDate) {
            taleSearchParams.datoAFO = dayjs(o.startDate).format('YYYYMM');
            taleSearchParams.datoBFO = dayjs(o.endDate).add(1, 'month').format('YYYYMM');
            taleSearchParams.datoCFO = dayjs(o.startDate).subtract(1, 'year').format('YYYYMM');
            taleSearchParams.datoDFO = dayjs(o.endDate).add(1, 'month').subtract(1, 'year').format('YYYYMM');
        }
        else {
            if (currentDate !== endDate) {
                taleSearchParams.datoA = dayjs(o.startDate).format('YYYYMM');
                taleSearchParams.datoB = dayjs(o.endDate).add(1, 'month').format('YYYYMM');
                taleSearchParams.datoC = dayjs(o.startDate).subtract(1, 'year').format('YYYYMM');
                taleSearchParams.datoD = dayjs(o.endDate).add(1, 'month').subtract(1, 'year').format('YYYYMM');
            }
            else {
                //
                taleSearchParams.datoA = dayjs(o.startDate).format('YYYYMM');
                taleSearchParams.datoB = dayjs(o.endDate).format('YYYYMM');
                taleSearchParams.datoC = dayjs(o.startDate).subtract(1, 'year').format('YYYYMM');
                taleSearchParams.datoD = dayjs(o.endDate).subtract(1, 'year').format('YYYYMM');
                //
                taleSearchParams.datoAFO = dayjs(o.endDate).format('YYYYMM');
                taleSearchParams.datoBFO = dayjs(o.endDate).add(1, 'month').format('YYYYMM');
                taleSearchParams.datoCFO = dayjs(o.endDate).subtract(1, 'year').format('YYYYMM');
                taleSearchParams.datoDFO = dayjs(o.endDate).add(1, 'month').subtract(1, 'year').format('YYYYMM');
            }
        }
        // Excel / PDF with the columns the table shows ("Alle kolonner").
        if (mediaType === 'pdf' || mediaType === 'excel') {
            taleSearchParams.columns = shownColumns(o.visning);
            setExporting(mediaType);
        }
        // The table's heading (dates, Brutto) changes when the new report has come (in the saga), and a
        // new click while one is loading replaces it (takeLatest).
        dispatch(actions.tallenesGetSalgsstatiskk(taleSearchParams));
    };

    //Clean up - Wipes out all reports
    const resetAllSalesReport = (selected: FirmNavnId[]) => {
        dispatch(actions.setSelectedKunde(new FirmNavnId(selected.length === 1 ? selected[0].firmID : 0, storeNames(selected))));
        dispatch(actions.setFullResetSalesReported());
        dispatch(actions.setSelectedSubKunde(new FirmNavnId(0, '')));
    }

    //
    return (
        <FormProvider {...methods}>
            <form>
                <input {...methods.register('listId')} hidden />
                <Box mb={1.5}>
                    <Typography className={classes.eyebrow}>Rapporter salg</Typography>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.2}>Salgsstatistikk</Typography>
                </Box>
                {/* Utplukk */}
                <Box className={classes.row}>
                    <Field label="Favorittliste" width={240}>
                        <Controller control={methods.control} name="fList" render={({ field: { onChange } }) => (
                            <Autocomplete size="small" className={classes.field}
                                onChange={(event, item) => { onChange(item); }}
                                onKeyUp={(e) => { methods.setValue(`betegnelse`, (e.target as any).value) }}
                                options={taleFavorittListeList ? taleFavorittListeList : []}
                                getOptionLabel={(item: any) => (item.betegnelse ? item.betegnelse : "")}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Velg eller opprett en ny" variant="outlined" />
                                )}
                            />)}
                        />
                    </Field>
                    <Field label="Butikk liste" width={380}>
                        <Controller control={methods.control} name={`bList`} render={({ field: { onChange, value } }) => (
                            <StorePicker compact className={classes.field} mainCompany={hovedfirma} companies={firmListFromAdmin ?? []}
                                value={value ?? []} onChange={(selected) => { onChange(selected); resetAllSalesReport(selected) }} />)}
                        />
                    </Field>
                    <Field label="Fra" width={150}>
                        <Controller control={methods.control} name={`startDate`} render={({ field: { onChange/*, value*/ } }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker className={classes.field}
                                    openTo="year"
                                    disableFuture={true}
                                    minDate={dayjs().set('year', 2022).set('month', 0)}
                                    maxDate={dayjs(date).endOf('month')}
                                    value={dayjs(methods.getValues('startDate'))}
                                    views={["year", "month"]}
                                    onChange={(item) => { onChange(item); methods.setValue('startDate', item as any) }}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        )}
                        />
                    </Field>
                    <Field label="Til" width={150}>
                        <Controller control={methods.control} name={`endDate`} render={({ field: { onChange/*, value */ } }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker className={classes.field}
                                    openTo="year"
                                    disableFuture={true}
                                    minDate={dayjs(methods.getValues('startDate'))}
                                    maxDate={dayjs(date).endOf('month')}
                                    value={dayjs(methods.getValues('endDate')).startOf('month')}
                                    views={["year", "month"]}
                                    onChange={(item) => { onChange(item); methods.setValue('endDate', (item as any).endOf('month')) }}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        )}
                        />
                    </Field>
                    <Field label="Visning" width={190}>
                        <Controller control={methods.control} name={`visning`} render={({ field: { onChange, value } }) => (
                            <Autocomplete size="small" className={classes.field} options={E.uVisning}
                                onChange={(event, item) => {
                                    onChange(item); methods.setValue('visning', item as any);
                                    dispatch(actions.setfavorittListeVisningType(item));
                                }}
                                value={value} getOptionLabel={(option) => option}
                                isOptionEqualToValue={(option, value: any) => value === undefined || option === value}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Velg visning" variant="outlined" />
                                )}
                            />)}
                        />
                    </Field>
                </Box>
                <Box className={classes.row} sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1.25 }}>
                    <Box display="flex" alignItems="center">
                        <FormControlLabel className={classes.checkbox} label="Vis brutto" control={<Controller control={methods.control}
                            name="brutto" render={({ field: { onChange, value } }) => (
                                <Checkbox size="small" onChange={(event, item) => {
                                    onChange(item);
                                    //
                                    if (item) {
                                        setIsBruttoFortjeneste(false);
                                        methods.setValue('bruttofortjeneste', false);
                                    } else {
                                        setIsBruttoFortjeneste(true);
                                    }
                                }} name={'brutto'} checked={value} />)}
                        />} />
                        {U.T_groups?.includes('17') && isBruttoFortjeneste ?
                            <FormControlLabel className={classes.checkbox} label="Vis bruttofortjeneste" control={<Controller control={methods.control}
                                name="bruttofortjeneste" render={({ field: { onChange, value } }) => (
                                    <Checkbox size="small" onChange={(event, item) => { onChange(item); }} name={'bruttofortjeneste'} checked={value} />)}
                            />} /> : null}
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button variant="contained" disableElevation className={classes.button} startIcon={<BarChartIcon fontSize="small" />}
                            disabled={buttonReportDisabled} onClick={methods.handleSubmit((o) => onSubmitGetSalesReport(o, 'web'))}
                            sx={{ backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' }, mr: 1 }}>
                            Hent salgsstatistikk
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<GridOnOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                            disabled={buttonReportDisabled || exporting !== null} onClick={methods.handleSubmit((o) => onSubmitGetSalesReport(o, 'excel'))}>
                            {exporting === 'excel' ? 'Lager…' : 'Excel'}
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<PrintOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                            disabled={buttonReportDisabled || exporting !== null} onClick={methods.handleSubmit((o) => onSubmitGetSalesReport(o, 'pdf'))}>
                            {exporting === 'pdf' ? 'Lager…' : 'PDF'}
                        </Button>
                        <Tooltip title={'En krevende SQL-operasjon hvis du har mye data. Å redusere datoperioden i utvalget bidrar til å oppnå raskere resultater.'}>
                            <span>
                                <Button variant="outlined" className={cx(classes.button, classes.outlined)} startIcon={<DataObjectIcon fontSize="small" />}
                                    disabled={buttonDataDisabled} onClick={methods.handleSubmit((o) => onSubmitGetSalesReport(o, 'data'))}>
                                    Rådata
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                {/* Favorittliste */}
                <Box className={classes.row}>
                    <Field label="Betegnelse" width={200}>
                        <TextField size="small" fullWidth placeholder="Navn på favorittliste" autoComplete="off" className={classes.field} inputProps={{ maxLength: 3 }}
                            {...methods.register("betegnelse", { required: false })}
                            onKeyUp={(e) => { checkIfItAlreadyExist((e.target as any).value); methods.setValue(`betegnelse`, (e.target as any).value) }}
                        />
                    </Field>
                    <Field label="Merknader" width={200}>
                        <TextField size="small" fullWidth placeholder="Valgfri merknad" autoComplete="off" className={classes.field} inputProps={{ maxLength: 3 }}
                            {...methods.register("merknader", { required: false })}
                        />
                    </Field>
                    <Button variant="contained" disableElevation className={classes.button} disabled={buttonSaveDisabled}
                        onClick={methods.handleSubmit(onSubmitSaveFavorite)}
                        sx={{ backgroundColor: '#002a4e', '&:hover': { backgroundColor: '#001b33' } }}>
                        Lagre favorittliste
                    </Button>
                    <Button variant="outlined" className={cx(classes.button, classes.outlined)} disabled={buttonDeleteDisabled}
                        onClick={methods.handleSubmit(onSubmitDeleteFavorite)}>
                        Fjern favorittliste
                    </Button>
                </Box>
            </form>
        </FormProvider>
    );
};

export default TallenesTaleTableToolbar;
